import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { ReactNode, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { DmFilter, GetAdminPostQuery, useGetAdminPostQuery } from '../../../api/client'
import MastheadBreadcrumb from '../../../components/MastheadBreadcrumb'
import PageMasthead from '../../../components/PageMasthead'
import Spinner from '../../../components/Spinner'
import ContentList from '../../../components/content/ContentList'
import SearchParameters from '../../../components/content/search/searchParameters'
import { faIconName } from '../../../util/classNames'
import ModerationBreakdown from './ModerationBreakdown'
import { PostTypeLabel, formatPublishTime } from './PostTable'
import { postCollectionNote, postPermalink } from './postPermalink'
import WarningCallout from '../../../components/callout/WarningCallout'
import ContentSearchBar from '../../../components/content/search/ContentSearchBar'
import PostConversation from '../../../components/conversation/PostConversation'
import { conversationViewEnabledState, userState } from '../../../store/DomainStore'
import { getClassifierSearchParam } from '../../../models/ClassifierSearchParam'

/**
 * One post: what we know about it, then every comment we hold under it via
 * the shared ContentList, so moderation actions work here exactly as they do
 * in the feed.
 */
export default function PostDetailPage() {
    const { postId: postIdParam } = useParams<{ postId: string }>()
    const postId = parseInt(postIdParam ?? '', 10)
    const conversationViewEnabled = useRecoilValue(conversationViewEnabledState)

    const { data, loading, error } = useGetAdminPostQuery({
        variables: { postId },
        skip: Number.isNaN(postId)
    })
    const post = data?.getAdminPost

    // The content search dates from the comment, not the post, and comments can
    // arrive years after publication, so the window is left wide open — the
    // post filter is the scope here, as contentIds is for the spam page.
    const searchParameters: SearchParameters | undefined = useMemo(() => {
        if (!post) return undefined
        return {
            keyword: '',
            moderation: 'all',
            selectedAccountIds: [post.accountId],
            selectedClassifiers: [],
            selectedGeneralClassifiers: [],
            selectedSocialMediaServices: [],
            selectedTeams: [],
            excludeOwnComments: false,
            dmFilter: DmFilter.All,
            postIds: [post.id],
            startDateTime: dayjs('2019-01-01'),
            endDateTime: dayjs('3000-01-02')
        }
    }, [post])

    if (Number.isNaN(postId)) {
        return <NotFound message="That is not a post id." />
    }
    if (loading && !post) {
        return (
            <div className="flex flex-1 items-center justify-center py-16">
                <Spinner size={6} />
            </div>
        )
    }
    if (error) {
        return <NotFound message={`Could not load post ${postId}. ${error.message}`} />
    }
    if (!post || !searchParameters) {
        return <NotFound message={`We hold no post with id ${postId}.`} />
    }

    const title = post.title.trim() || 'Untitled post'
    const profileName =
        post.accountName || post.serviceAccountUsername || `Profile ${post.accountId}`
    const permalink = postPermalink(post)
    const collectionNote = postCollectionNote(post.socialMediaServiceShortName)
    // Named so the gap between the two counts can be read against when the
    // platform figure was taken.
    const fetchedAt = post.platformCommentCountFetchedAt
        ? ` at ${dayjs(post.platformCommentCountFetchedAt).format('D MMM YYYY, HH:mm')}`
        : ''

    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-ink-50">
            <PageMasthead
                breadcrumb={<MastheadBreadcrumb to="/admin/post" label="Posts" />}
                title={title}
                tabTitle={`${title} · Post admin`}
                actions={
                    permalink && (
                        <a
                            href={permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700 shadow-sm hover:bg-ink-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-arwen-500"
                        >
                            Open on {post.socialMediaServiceShortName}
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                        </a>
                    )
                }
            />

            <div className="flex flex-col gap-4 px-6 py-4">
                {collectionNote && <WarningCallout>{collectionNote}</WarningCallout>}
                <section className="rounded-lg border border-ink-200 bg-white p-5 shadow-sm">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm md:grid-cols-4">
                        <Field label="Profile">
                            <span className="inline-flex items-center gap-2">
                                <i
                                    className={`fab ${faIconName(post.socialMediaServiceShortName, true)} text-base`}
                                    aria-hidden="true"
                                />
                                <Link
                                    to={`/account/${post.accountId}`}
                                    className="text-ink-900 hover:text-arwen-600"
                                >
                                    {profileName}
                                </Link>
                            </span>
                        </Field>
                        <Field label="Team">
                            {post.teamName ? (
                                <Link
                                    to={`/admin/team/edit/${post.teamId}`}
                                    className="text-ink-900 hover:text-arwen-600"
                                >
                                    {post.teamName}
                                </Link>
                            ) : (
                                <span className="text-ink-500">No team</span>
                            )}
                        </Field>
                        <Field label="Published">
                            {formatPublishTime(post.publishTime)}
                            <span className="block text-xs text-ink-500">
                                Created {formatPublishTime(post.createdAt)}
                            </span>
                        </Field>
                        <Field label="Type">
                            <span className="inline-flex items-center gap-2">
                                <PostTypeLabel isAd={post.isAd} />
                                <span className="text-ink-500">{post.postType}</span>
                            </span>
                        </Field>
                        <Field label="Post id">
                            <span className="tabular-nums">{post.id}</span>
                            <span
                                className="block truncate text-xs text-ink-500"
                                title={post.servicePostId}
                            >
                                {post.servicePostId}
                            </span>
                        </Field>
                        <Field label="Last fetched">
                            {post.lastFetched ? (
                                dayjs(post.lastFetched).format('D MMM YYYY, HH:mm')
                            ) : (
                                <span className="text-ink-500">Never</span>
                            )}
                        </Field>
                        <Field label="Collection">
                            {post.isDeleted ? (
                                <span className="text-ink-500">Post deleted</span>
                            ) : post.shouldFetch ? (
                                'Fetching'
                            ) : (
                                <span className="text-ink-500">Paused</span>
                            )}
                        </Field>
                        <Field label="Comments">
                            <span className="tabular-nums">
                                {post.collectedCommentCount.toLocaleString('en-GB')} collected
                            </span>
                            <span className="block text-xs text-ink-500 tabular-nums">
                                {post.platformCommentCount !== null &&
                                post.platformCommentCount !== undefined
                                    ? `${post.platformCommentCount.toLocaleString('en-GB')} reported by the platform${fetchedAt}`
                                    : 'No platform count'}
                            </span>
                        </Field>
                    </dl>
                    <div className="mt-5 border-t border-ink-100 pt-4">
                        <ModerationBreakdown
                            variant="detail"
                            collected={post.collectedCommentCount}
                            safe={post.safeCount}
                            suspect={post.suspectCount}
                            severe={post.severeCount}
                        />
                    </div>
                </section>

                {conversationViewEnabled ? (
                    // Keyed on the post so moving to another post starts from its own
                    // unfiltered list rather than carrying this post's filters over.
                    <PostComments key={post.id} post={post} initial={searchParameters} />
                ) : (
                    <section>
                        <h2 className="font-['Jost',system-ui,sans-serif] !font-medium mb-1 text-base text-ink-900">
                            Comments under this post
                        </h2>
                        <p className="mb-3 text-sm text-ink-500">
                            Moderated comments only, newest first. The same actions as the feed.
                        </p>
                        <ContentList
                            searchParameters={searchParameters}
                            showOwningAccount={false}
                            enableBulkActions={false}
                        />
                    </section>
                )}
            </div>
        </div>
    )
}

type CommentsView = 'chronological' | 'threaded'

const VIEW_OPTIONS: { key: CommentsView; label: string }[] = [
    { key: 'chronological', label: 'Chronological' },
    { key: 'threaded', label: 'Threaded' }
]

type PostForComments = NonNullable<GetAdminPostQuery['getAdminPost']>

/**
 * The comments section behind the conversation-view toggle:
 * one set of filters driving two tabs, the flat newest-first list and the
 * threaded view. The tab lives in `?view=` so a link or reload keeps it; the
 * filters are page state, so switching tabs keeps them.
 */
/**
 * The classifier picker sets only `selectedGeneralClassifiers`, but both tabs search
 * on `selectedClassifiers`. The feed derives one from the other where it reads its
 * query string (useContentQueryParams); this page has no query-string filters, so
 * it derives them here, or picking a classifier would filter nothing.
 */
export function withDerivedClassifiers(
    current: SearchParameters,
    updated: Partial<SearchParameters>
): SearchParameters {
    const merged = { ...current, ...updated }
    return {
        ...merged,
        selectedClassifiers: merged.selectedGeneralClassifiers.map((classifier) =>
            getClassifierSearchParam(classifier)
        )
    }
}

export function PostComments({
    post,
    initial
}: {
    post: PostForComments
    initial: SearchParameters
}) {
    const user = useRecoilValue(userState)
    const [searchParameters, setSearchParameters] = useState<SearchParameters>(initial)
    const [urlParams, setUrlParams] = useSearchParams()
    const view: CommentsView = urlParams.get('view') === 'threaded' ? 'threaded' : 'chronological'

    const selectView = (key: string) => {
        const next = new URLSearchParams(urlParams)
        next.set('view', key)
        // Replace, not push: a tab is a view of this page, not somewhere Back should step through.
        setUrlParams(next, { replace: true })
    }

    return (
        <section>
            <h2 className="font-['Jost',system-ui,sans-serif] !font-medium mb-1 text-base text-ink-900">
                Comments under this post
            </h2>
            <p className="text-sm text-ink-500">
                {view === 'threaded'
                    ? 'Top-level comments newest first, each with its replies. Comments the filters exclude show as a marker where they fell.'
                    : 'Moderated comments only, newest first. The same actions as the feed.'}
            </p>
            {/* The bar pads itself for the feed page; pull it back into this column. */}
            <div className="-mx-4">
                <ContentSearchBar
                    // The platform picker is hidden here, and it is the only control that
                    // reads the list of platforms; the post fixes the platform anyway.
                    allSocialMediaServices={[]}
                    searchParameters={searchParameters}
                    onSearchParameterChange={(updated) =>
                        setSearchParameters((current) => withDerivedClassifiers(current, updated))
                    }
                    showProfileSearch={false}
                    showReadStatusSearch={user.canUseReadStatus}
                    showSocialMediaServiceSearch={false}
                    showTeamSearch={false}
                />
            </div>
            {/* Underlined tabs rather than a filled toggle: the view is a way of reading
                the list, not an action, so it should not compete with the red CTAs. */}
            <div role="tablist" aria-label="Comment view" className="mb-4 flex gap-6 border-b border-ink-200">
                {VIEW_OPTIONS.map((option) => {
                    const selected = view === option.key
                    return (
                        <button
                            key={option.key}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            onClick={() => selectView(option.key)}
                            className={`-mb-px border-b-2 py-2 text-sm ${
                                selected
                                    ? 'border-arwen-600 font-medium text-ink-900'
                                    : 'border-transparent text-ink-500 hover:text-ink-800'
                            }`}
                        >
                            {option.label}
                        </button>
                    )
                })}
            </div>
            {view === 'threaded' ? (
                <PostConversation post={post} searchParameters={searchParameters} />
            ) : (
                <ContentList
                    searchParameters={searchParameters}
                    showOwningAccount={false}
                    enableBulkActions={false}
                />
            )}
        </section>
    )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">
                {label}
            </dt>
            <dd className="mt-0.5 text-ink-900">{children}</dd>
        </div>
    )
}

function NotFound({ message }: { message: string }) {
    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-ink-50">
            <PageMasthead
                breadcrumb={<MastheadBreadcrumb to="/admin/post" label="Posts" />}
                title="Post not found"
                tabTitle="Post admin"
            />
            <div className="px-6 py-4">
                <div className="rounded-lg border border-ink-200 bg-white p-6 text-sm text-ink-700">
                    {message}
                </div>
            </div>
        </div>
    )
}
